/**
 * Auth-aware loader for full-resolution slide images.
 *
 * The plain ``/api/slide-sense/slides/<id>/image`` URL works as an
 * ``<img src>`` in production — Azure App Service injects auth headers
 * for every request, including resource loads. In local dev (and any
 * deployment without App Service auth) those headers are missing, so a
 * direct ``<img src>`` 401s. This hook side-steps that by fetching the
 * PNG via the axios client (which carries the Bearer token), converts
 * it to a blob URL, and caches the result.
 *
 * The cache is a module-level Zustand store keyed by slide id. Mirrors
 * ``useThumbnailCache``'s shape so consumers feel the same. Object URLs
 * are not revoked — they are cheap and bounded by session lifetime.
 */

import { useEffect } from "react"

import { create } from "zustand"

import { api } from "@/lib/api-client"

interface SlideImageCacheState {
  urls: Map<string, string>
  inFlight: Set<string>
  failed: Set<string>
  setUrl: (slideId: string, url: string) => void
  markInFlight: (slideId: string) => void
  clearInFlight: (slideId: string) => void
  markFailed: (slideId: string) => void
  isPending: (slideId: string) => boolean
  hasFailed: (slideId: string) => boolean
}

const useSlideImageCache = create<SlideImageCacheState>((set, get) => ({
  urls: new Map(),
  inFlight: new Set(),
  failed: new Set(),
  setUrl: (slideId, url) =>
    set((state) => {
      const next = new Map(state.urls)
      next.set(slideId, url)
      return { urls: next }
    }),
  markInFlight: (slideId) =>
    set((state) => {
      const next = new Set(state.inFlight)
      next.add(slideId)
      return { inFlight: next }
    }),
  clearInFlight: (slideId) =>
    set((state) => {
      const next = new Set(state.inFlight)
      next.delete(slideId)
      return { inFlight: next }
    }),
  markFailed: (slideId) =>
    set((state) => {
      const next = new Set(state.failed)
      next.add(slideId)
      return { failed: next }
    }),
  isPending: (slideId) => get().inFlight.has(slideId),
  hasFailed: (slideId) => get().failed.has(slideId),
}))

/**
 * @param speculative When true, this is an off-screen preload (not a slide
 *   the user is looking at yet). A speculative failure must NOT write the
 *   terminal `failed` set: that set is never evicted, so poisoning it here
 *   would permanently blank a slide before the user ever navigates to it
 *   (a transient 401/timeout during preload should not block the later
 *   real load). The on-demand load (`speculative=false`) still marks
 *   failed so the UI can show its fallback. (#2153 review)
 */
async function fetchSlideImageBlob(slideId: string, speculative = false): Promise<void> {
  const cache = useSlideImageCache.getState()
  if (cache.urls.has(slideId) || cache.isPending(slideId) || cache.hasFailed(slideId)) return
  cache.markInFlight(slideId)
  try {
    // axios applies the request interceptor (Bearer token) and the
    // response interceptor unwraps ``response.data`` from successful
    // calls — so the resolved value here is the Blob directly, not a
    // wrapping AxiosResponse.
    const blob = (await api.get(`/slide-sense/slides/${slideId}/image`, {
      responseType: "blob",
    })) as unknown as Blob
    if (blob.size === 0) {
      // Backend returns 204-with-empty-body for slides whose PNG hasn't
      // been rendered yet (or was deleted). Treat as failed so the
      // caller falls back to the broken-image placeholder.
      if (!speculative) useSlideImageCache.getState().markFailed(slideId)
      return
    }
    const objectUrl = URL.createObjectURL(blob)
    useSlideImageCache.getState().setUrl(slideId, objectUrl)
  } catch (err) {
    // Log with the slide id so "a slide went blank mid-presentation" is
    // debuggable instead of a silent black frame.
    console.error(`[slide-image] failed to load slide ${slideId}`, err)
    if (!speculative) useSlideImageCache.getState().markFailed(slideId)
  } finally {
    useSlideImageCache.getState().clearInFlight(slideId)
  }
}

/**
 * Imperatively warm the blob cache for a slide (e.g. preloading the next
 * slides during a presentation). Safe to call repeatedly — it no-ops when
 * the image is already cached, in flight, or known-failed.
 */
export function prefetchSlideImageBlob(slideId: string | undefined): void {
  if (!slideId) return
  // speculative=true → a preload failure won't poison the slide for the
  // later on-demand load.
  void fetchSlideImageBlob(slideId, true)
}

/** Drop a cached full-res blob URL (e.g. after in-place reprocess). */
export function evictSlideImageCache(slideId: string): void {
  const cache = useSlideImageCache.getState()
  const existing = cache.urls.get(slideId)
  if (existing) {
    URL.revokeObjectURL(existing)
  }
  useSlideImageCache.setState((state) => {
    const urls = new Map(state.urls)
    urls.delete(slideId)
    const inFlight = new Set(state.inFlight)
    inFlight.delete(slideId)
    const failed = new Set(state.failed)
    failed.delete(slideId)
    return { urls, inFlight, failed }
  })
}

export function useSlideImageBlob(slideId: string | undefined) {
  const url = useSlideImageCache((s) => (slideId ? s.urls.get(slideId) : undefined))
  const isPending = useSlideImageCache((s) => (slideId ? s.inFlight.has(slideId) : false))
  const hasFailed = useSlideImageCache((s) => (slideId ? s.failed.has(slideId) : false))

  useEffect(() => {
    if (!slideId) return
    if (url || isPending || hasFailed) return
    void fetchSlideImageBlob(slideId)
  }, [slideId, url, isPending, hasFailed])

  return { url: url ?? null, error: hasFailed, isPending }
}
